import { Check } from "@/icons/Check.tsx";
import { CircleCheck } from "@/icons/CircleCheck.tsx";
import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { CircleXMark } from "@/icons/CircleXMark.tsx";
import { Exclamation } from "@/icons/Exclamation.tsx";
import { FolderImage } from "@/icons/FolderImage.tsx";
import { TrashCan } from "@/icons/TrashCan.tsx";
import { Upload } from "@/icons/Upload.tsx";
import { XMark } from "@/icons/XMark.tsx";
import api from "@/lib/api.ts";
import { LoraModel, LoraResponse } from "@/models/lora.model.ts";
import { PhotoModel } from "@/models/photo.model.ts";
import { Loading } from "@/ui/shared/Loading.tsx";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Divider,
  Group,
  Image,
  List,
  LoadingOverlay,
  Progress,
  Text,
  ThemeIcon,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosProgressEvent, AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import avoid1 from "@/assets/lora-samples/avoid_1.jpeg";
import avoid2 from "@/assets/lora-samples/avoid_2.jpeg";
import avoid3 from "@/assets/lora-samples/avoid_3.jpeg";
import avoid4 from "@/assets/lora-samples/avoid_4.jpeg";
import avoid5 from "@/assets/lora-samples/avoid_5.jpeg";
import ok1 from "@/assets/lora-samples/ok_1.jpeg";
import ok2 from "@/assets/lora-samples/ok_2.jpeg";
import ok3 from "@/assets/lora-samples/ok_3.jpeg";
import ok4 from "@/assets/lora-samples/ok_4.jpeg";
import ok5 from "@/assets/lora-samples/ok_5.jpeg";
import ok6 from "@/assets/lora-samples/ok_6.jpeg";
import ok7 from "@/assets/lora-samples/ok_7.jpeg";
import ok8 from "@/assets/lora-samples/ok_8.jpeg";
import ok9 from "@/assets/lora-samples/ok_9.jpeg";
import ok10 from "@/assets/lora-samples/ok_10.jpeg";
import notOk1 from "@/assets/lora-samples/not-ok_1.jpeg";
import notOk2 from "@/assets/lora-samples/not-ok_2.jpeg";
import notOk3 from "@/assets/lora-samples/not-ok_3.jpeg";
import notOk4 from "@/assets/lora-samples/not-ok_4.jpeg";
import notOk5 from "@/assets/lora-samples/not-ok_5.jpeg";
import notOk6 from "@/assets/lora-samples/not-ok_6.jpeg";
import { AnimatePresence, motion } from "framer-motion";
import Lightbox from "yet-another-react-lightbox";
import { Inline } from "yet-another-react-lightbox/plugins";

const stepper = [
  {
    title: "1. Introduction",
    description: "Read carefully before starting",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <div className="flex flex-col gap-3">
          <Alert
            icon={<CircleInfo size={24} />}
            styles={{
              message: { fontSize: 16 },
            }}
            bd="1px dashed violet.8"
            title="Quality input equals quality output"
          >
            For optimal results from our service, please follow our photo
            guidelines closely. High-quality images help us deliver high-quality
            outcomes.
          </Alert>
        </div>
      </Card.Section>
    ),
  },
  {
    title: "2. Instructions",
    description: "Acceptable photos",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <div className="flex flex-wrap justify-center gap-3 mb-3">
          <div className="flex flex-col w-full flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <ThemeIcon color="green" size={22} radius="xl">
                <CircleCheck />
              </ThemeIcon>
              <Text fw={500} size="lg">
                Photos to upload:
              </Text>
            </div>
            <div className="flex justify-center w-full">
              <Lightbox
                styles={{
                  root: {
                    borderRadius: "0.375rem",
                    overflow: "hidden",
                  },
                }}
                carousel={{
                  padding: 0,
                  spacing: 0,
                  imageFit: "cover",
                }}
                plugins={[Inline]}
                inline={{
                  style: {
                    width: "100%",
                    maxWidth: "900px",
                    aspectRatio: "3 / 2",
                  },
                }}
                slides={[
                  { src: ok1 },
                  { src: ok2 },
                  { src: ok3 },
                  { src: ok4 },
                  { src: ok5 },
                  { src: ok6 },
                  { src: ok7 },
                  { src: ok8 },
                  { src: ok9 },
                  { src: ok10 },
                ]}
                animation={{ fade: 0 }}
                controller={{
                  closeOnPullDown: true,
                  closeOnBackdropClick: true,
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <List spacing="xs" size="md" center icon={<Check size={20} />}>
                <List.Item>High quality</List.Item>
                <List.Item>Recent photos</List.Item>
                <List.Item>Clear face details</List.Item>
              </List>

              <List spacing="xs" size="md" center icon={<Check size={20} />}>
                <List.Item>Natural expressions</List.Item>
                <List.Item>Good lighting</List.Item>
                <List.Item>Various backgrounds</List.Item>
              </List>

              <List spacing="xs" size="md" center icon={<Check size={20} />}>
                <List.Item>Various clothing</List.Item>
              </List>
            </div>
          </div>
        </div>
      </Card.Section>
    ),
  },
  {
    title: "2. Instructions",
    description: "Unacceptable photos",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <div className="flex flex-wrap justify-center gap-3 mb-3">
          <div className="flex flex-col w-full flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <ThemeIcon color="red" size={22} radius="xl">
                <CircleXMark />
              </ThemeIcon>
              <Text fw={500} size="lg">
                Photos NOT to upload:
              </Text>
            </div>
            <div className="flex justify-center w-full">
              <Lightbox
                styles={{
                  root: {
                    borderRadius: "0.375rem",
                    overflow: "hidden",
                  },
                }}
                carousel={{
                  padding: 0,
                  spacing: 0,
                  imageFit: "cover",
                }}
                plugins={[Inline]}
                inline={{
                  style: {
                    width: "100%",
                    maxWidth: "900px",
                    aspectRatio: "3 / 2",
                  },
                }}
                slides={[
                  { src: notOk1 },
                  { src: notOk2 },
                  { src: notOk3 },
                  { src: notOk4 },
                  { src: notOk5 },
                  { src: notOk6 },
                ]}
                animation={{ fade: 0 }}
                controller={{
                  closeOnPullDown: true,
                  closeOnBackdropClick: true,
                }}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <List spacing="xs" size="md" center icon={<XMark size={20} />}>
                <List.Item>Low quality</List.Item>
                <List.Item>Blurry photos</List.Item>
                <List.Item>Old photos</List.Item>
              </List>

              <List spacing="xs" size="md" center icon={<XMark size={20} />}>
                <List.Item>Multiple people</List.Item>
                <List.Item>Distant photos</List.Item>
                <List.Item>Sunglasses</List.Item>
              </List>

              <List spacing="xs" size="md" center icon={<XMark size={20} />}>
                <List.Item>Bad lighting</List.Item>
                <List.Item>Black and white</List.Item>
                <List.Item>Hats</List.Item>
              </List>
            </div>
          </div>
        </div>
      </Card.Section>
    ),
  },
  {
    title: "2. Instructions",
    description: "Photos to avoid",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <div className="flex flex-wrap justify-center gap-3 mb-3">
          <div className="flex flex-col w-full flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <ThemeIcon color="yellow" size={22} radius="xl">
                <CircleInfo />
              </ThemeIcon>
              <Text fw={500} size="lg">
                Photos to avoid:
              </Text>
            </div>
            <div className="flex justify-center w-full">
              <Lightbox
                styles={{
                  root: {
                    borderRadius: "0.375rem",
                    overflow: "hidden",
                  },
                }}
                carousel={{
                  padding: 0,
                  spacing: 0,
                  imageFit: "cover",
                }}
                plugins={[Inline]}
                inline={{
                  style: {
                    width: "100%",
                    maxWidth: "900px",
                    aspectRatio: "3 / 2",
                  },
                }}
                slides={[
                  { src: avoid1 },
                  { src: avoid2 },
                  { src: avoid3 },
                  { src: avoid4 },
                  { src: avoid5 },
                ]}
                animation={{ fade: 0 }}
                controller={{
                  closeOnPullDown: true,
                  closeOnBackdropClick: true,
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <List
                spacing="xs"
                size="md"
                center
                icon={<Exclamation size={20} />}
              >
                <List.Item>Excess makeup</List.Item>
                <List.Item>Jewellery</List.Item>
              </List>

              <List
                spacing="xs"
                size="md"
                center
                icon={<Exclamation size={20} />}
              >
                <List.Item>Face is off canvas</List.Item>
                <List.Item>Nudes</List.Item>
              </List>
            </div>
          </div>
        </div>
      </Card.Section>
    ),
  },
  {
    title: "3. Add photos",
    description: "Portraits",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <div className="flex flex-wrap gap-3 mb-3">
          <div className="flex flex-col flex-wrap w-full gap-4">
            <div className="flex items-center gap-2 mx-auto">
              <Text fw={500} size="xl">
                Add <span className="font-bold">10 to 15</span> photos of the
                character
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col gap-3">
                <Text fw={500} size="lg">
                  5-10 close-up photos:
                </Text>

                <Divider />

                <List
                  icon={<CircleCheck size={18} />}
                  spacing="md"
                  size="md"
                  center
                >
                  <List.Item>Looking straight into the camera</List.Item>
                  <List.Item>From various angles</List.Item>
                  <List.Item>Expressing different emotions</List.Item>
                  <List.Item>With various backgrounds</List.Item>
                </List>
              </div>

              <div className="flex flex-col gap-3">
                <Text fw={500} size="lg">
                  3-5 mid-range photos:
                </Text>

                <Divider />

                <List
                  icon={<CircleCheck size={18} />}
                  spacing="md"
                  size="md"
                  center
                >
                  <List.Item>From various angles</List.Item>
                  <List.Item>With various backgrounds</List.Item>
                  <List.Item>With various clothes</List.Item>
                </List>
              </div>

              <div className="flex flex-col gap-3">
                <Text fw={500} size="lg">
                  2-3 full-body photos:
                </Text>

                <Divider />

                <List
                  icon={<CircleCheck size={18} />}
                  spacing="md"
                  size="md"
                  center
                >
                  <List.Item>From various angles</List.Item>
                  <List.Item>With various backgrounds</List.Item>
                  <List.Item>With various clothes</List.Item>
                </List>
              </div>
            </div>
          </div>
        </div>
      </Card.Section>
    ),
  },
  {
    title: "4. Confirmation",
    description: "Review and finalize",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <Alert
          styles={{
            message: { fontSize: 16 },
          }}
          bd="1px dashed violet.8"
          icon={<CircleInfo size={24} />}
        >
          Confirm the photos you have selected and remove any photos you do not
          want to use.
        </Alert>
      </Card.Section>
    ),
  },
  {
    title: "5. Train",
    description: "Start training",
    info: (
      <Card.Section className="relative" withBorder inheritPadding py="md">
        <Alert
          styles={{
            message: { fontSize: 16 },
          }}
          color="green"
          bd="1px dashed green.8"
          icon={<CircleInfo size={24} />}
        >
          Your character is now ready to be trained. Click on the Train button
          to start the training process.
        </Alert>
      </Card.Section>
    ),
  },
];

export function TrainerUploader() {
  const theme = useMantineTheme();
  const { loraUuid } = useParams();
  const navigate = useNavigate();

  const loraUrl = `loras/${loraUuid}?include=photos`;

  const { data, status, refetch } = useQuery({
    queryKey: [loraUrl],
    queryFn: (): Promise<AxiosResponse<LoraResponse>> => api.get(loraUrl),
    retry: 1,
    select: (data) => {
      return data.data.data;
    },
  });
  const lora: LoraModel | null = data || null;

  useEffect(() => {
    if (!lora) {
      return;
    }

    if (lora.photos.data.length > 0) {
      setActive(4);
    }
  }, [lora]);

  const [active, setActive] = useState<number>(0);
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);
  const nextStep = () =>
    setActive((current: number): number =>
      current < stepper.length - 1 ? current + 1 : current,
    );
  const prevStep = () =>
    setActive((current: number): number =>
      current > 0 ? current - 1 : current,
    );
  const [files, setFiles] = useState<FileWithPath[]>([]);

  const filesMutation = useMutation({
    mutationFn: (files: FileWithPath[]) => {
      return api.post(`loras/${loraUuid}/photos`, files, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          setUploadPercentage(
            progressEvent.progress ? 100 * progressEvent.progress : 0,
          );
        },
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Success!",
        message: "Your photos have been uploaded successfully.",
        color: "green",
      });
      refetch();
      setFiles([]);
    },
    onSettled: () => {
      setUploadPercentage(0);
    },
  });

  const trainMutation = useMutation({
    mutationFn: () => {
      return api.post(`loras/${loraUuid}/train`, {});
    },
    onSuccess: () => {
      notifications.show({
        title: "Success!",
        message:
          "Your training process has been initiated successfully. You will get notified when it is completed.",
        color: "green",
      });
      navigate("/characters");
    },
  });

  const photoDeleteMutation = useMutation({
    mutationFn: (photo: PhotoModel) => {
      return api.delete(`/photos/${photo.id}`);
    },
    onSuccess: () => {
      refetch();
    },
  });

  if (status === "error") {
    return (
      <Alert icon={<CircleXMark />} color="red" title="Something went wrong.">
        Unable to load your characters. Please try again later.
      </Alert>
    );
  }

  if (!lora) {
    return <Loading />;
  }

  const photosCounter = lora?.photos.data.length || 0;

  const uploadingScreens = active === 4;
  const trainScreen = active === stepper.length - 1;
  const totalPhotos = lora.photos.data.length + files.length;

  const stepperButtons = (
    <>
      <Button variant="default" disabled={active === 0} onClick={prevStep}>
        Back
      </Button>
      {active !== stepper.length - 1 && (
        <Button
          disabled={uploadingScreens && lora.photos.data.length < 10}
          onClick={nextStep}
        >
          Next
        </Button>
      )}
      {active === stepper.length - 1 && (
        <Button
          loading={trainMutation.isPending}
          disabled={trainMutation.isPending || photosCounter < 10}
          onClick={() => trainMutation.mutate()}
        >
          Train
        </Button>
      )}
    </>
  );

  if (status === "pending") {
    return <Loading />;
  }

  const previews = (
    <AnimatePresence initial={false} mode="popLayout">
      {lora.photos.data.map((photo: PhotoModel) => {
        return (
          <motion.div
            key={photo.id}
            layout
            whileHover={{ scale: 1.025 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative"
          >
            <Image
              h={300}
              w={300}
              src={photo.url}
              radius="md"
              bd="1px solid gray.7"
            />
            <div className="absolute top-0 right-0 p-4">
              <Tooltip label="Remove photo" withArrow>
                <ActionIcon
                  disabled={photoDeleteMutation.isPending}
                  size="lg"
                  variant="default"
                  onClick={() => photoDeleteMutation.mutate(photo)}
                >
                  <TrashCan style={{ color: theme.colors.red[6] }} />
                </ActionIcon>
              </Tooltip>
            </div>
          </motion.div>
        );
      })}
    </AnimatePresence>
  );

  return (
    <div className="flex flex-col gap-5 max-w-5xl mx-auto">
      <Card className="flex flex-1 w-100" padding="lg" withBorder>
        <Card.Section withBorder inheritPadding py="md">
          <div className="flex justify-between items-center">
            <Text fw={500} size="lg">
              {stepper[active].title}
            </Text>

            <div className="flex items-center gap-2">{stepperButtons}</div>
          </div>
        </Card.Section>

        {uploadPercentage > 0 && (
          <Card.Section withBorder inheritPadding py="md">
            <Progress.Root transitionDuration={500} size="lg">
              <Progress.Section
                aria-label="Uploading progress"
                value={uploadPercentage}
                animated
                striped
                color="violet"
              />
            </Progress.Root>
          </Card.Section>
        )}

        {uploadingScreens && (
          <Card.Section withBorder inheritPadding py="md">
            <Progress.Root transitionDuration={500} size="lg">
              <Progress.Section
                aria-label="Uploading progress"
                value={(100 * totalPhotos) / 15}
                color="violet"
              />
            </Progress.Root>
          </Card.Section>
        )}

        {stepper[active].info}

        {(active === 4 || active === 5) && (
          <Card.Section className="relative" withBorder inheritPadding py="md">
            <LoadingOverlay
              visible={filesMutation.isPending}
              loaderProps={{ color: "violet", type: "bars" }}
              zIndex={5}
              overlayProps={{ radius: "sm", blur: 1 }}
            />

            <div className="flex flex-col gap-3">
              {trainScreen && photosCounter < 10 && (
                <Alert
                  styles={{
                    message: { fontSize: 16 },
                  }}
                  bd="1px dashed yellow.8"
                  title="Warning!"
                  color="yellow"
                >
                  You need to upload at least 10 photos before training your
                  character.
                </Alert>
              )}

              {uploadingScreens && (
                <Dropzone
                  onDrop={(newFiles) => {
                    const existingFilesCount =
                      files.length + lora.photos.data.length;

                    if (existingFilesCount === 20) {
                      notifications.show({
                        title: "Error!",
                        message: "You can only attach a maximum of 20 photos.",
                        color: "red",
                      });
                      return;
                    }

                    setFiles((prevFiles) => {
                      const filteredFiles = newFiles.filter(
                        (nf) => !prevFiles.find((pf) => pf.path === nf.path),
                      );
                      const slicedNewFiles = filteredFiles.slice(
                        0,
                        20 - existingFilesCount,
                      );

                      // Helper function to split files into chunks of a given size
                      const chunkFiles = (
                        files: File[],
                        chunkSize: number,
                      ): File[][] => {
                        const chunks: File[][] = [];
                        for (let i = 0; i < files.length; i += chunkSize) {
                          chunks.push(files.slice(i, i + chunkSize));
                        }
                        return chunks;
                      };

                      // Split new files into chunks of 5 and process them
                      const fileChunks = chunkFiles(newFiles, 5);

                      fileChunks.forEach((chunk) => {
                        filesMutation.mutate(chunk);
                      });

                      return [...prevFiles, ...slicedNewFiles];
                    });
                  }}
                  onReject={(files) => console.log("rejected files", files)}
                  maxSize={15 * 1024 ** 2}
                  accept={IMAGE_MIME_TYPE}
                >
                  <Group
                    justify="center"
                    gap="xs"
                    mih={220}
                    style={{ pointerEvents: "none" }}
                  >
                    <Dropzone.Accept>
                      <Upload
                        size={52}
                        style={{
                          color: "var(--mantine-color-blue-6)",
                        }}
                      />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <CircleXMark
                        size={52}
                        style={{
                          color: "var(--mantine-color-red-6)",
                        }}
                      />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <FolderImage size={52} />
                    </Dropzone.Idle>

                    <div className="flex flex-col text-center md:text-start">
                      <Text size="xl">Drag photos here or click to select</Text>
                      <Text size="md" c="dimmed">
                        Attach 10 to 15 files, each file should not exceed 15mb
                      </Text>
                    </div>
                  </Group>
                </Dropzone>
              )}
              {(active === 4 || active === 5) && previews && (
                <div className="flex flex-wrap justify-center gap-3">
                  {previews}
                </div>
              )}
            </div>
          </Card.Section>
        )}
      </Card>
    </div>
  );
}
